import React, { ReactNode, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { differenceInDays } from 'date-fns';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';

import { useDispatch, useSelector } from '../../store/utils';
import { isSolidsunIframe } from '../../utils/url-layout-modes';
import { Group } from '../../organisms/group';
import { usePremiumDialog } from '../../organisms/premium/premium-dialog';
import { trpc } from '../../api/trpc';
import { colors } from '../../theme/palette';
import { Close } from '../../atoms/icons/close';
import { variablesChange } from '../../store/actions';
import { DialogClose } from '../../atoms/dialog-close';
import { License } from '../../shared';

type Props = {
  variant?: 'settings' | 'default';
  inactiveTitle?: ReactNode;
  inactiveSubtitle?: ReactNode;
};

export const PremiumBanner = ({
  variant = 'default',
  inactiveTitle,
  inactiveSubtitle,
}: Props) => {
  const [
    premiumActive,
    devPremiumOverride,
    premiumBannerHiddenPermanently,
    premiumBannerHiddenAt,
    license,
  ] = useSelector(
    ({
      variables: {
        PREMIUM,
        DEV_PREMIUM_OVERRIDE,
        PREMIUM_BANNER_HIDDEN_PERMANENTLY,
        PREMIUM_BANNER_HIDDEN_AT,
        LICENSE,
      },
    }) =>
      [
        PREMIUM,
        DEV_PREMIUM_OVERRIDE,
        PREMIUM_BANNER_HIDDEN_PERMANENTLY,
        PREMIUM_BANNER_HIDDEN_AT,
        LICENSE,
      ] as const
  );

  const { dialog, open } = usePremiumDialog();
  const { onHide, hideDialog } = useHidePremiumBanner({
    openPremiumDialog: open,
  });

  const { mutateAsync: getManagementLink } =
    trpc.subscriptions.getManagementLink.useMutation();

  const onOpenManagement = async () => {
    try {
      const { link } = await getManagementLink({ url: window.location.href });
      window.location.href = link;
    } catch (error) {
      alert(
        'Došlo k chybě při získávání odkazu na předplatné. Zkuste to prosím znovu později.'
      );
    }
  };

  const activeContent = (
    <>
      <Typography variant="h3">
        <FormattedMessage
          id="premium.banner.active.title"
          defaultMessage="Moje Infigy Plus"
        />
      </Typography>

      <Typography variant="body1" sx={{ maxWidth: 400, mb: 2 }}>
        <FormattedMessage
          id="premium.banner.active.subtitle"
          defaultMessage="Předplatné Infigy Plus je aktivní. Můžete si užívat všech vychytávek, které jsou díky němu v aplikaci navíc."
        />
      </Typography>

      <Box>
        {devPremiumOverride ? (
          <Typography variant="body1">
            <FormattedMessage
              id="premium.banner.active.dev_mode"
              defaultMessage="Předplatné je aktivní pro vývoj"
            />
          </Typography>
        ) : license === License.INDUSTRIAL ? null : (
          <Button
            variant="contained"
            size="small"
            onClick={onOpenManagement}
            sx={{ textTransform: 'none' }}
          >
            <FormattedMessage
              id="premium.banner.active.button"
              defaultMessage="Spravovat předplatné"
            />
          </Button>
        )}
      </Box>
    </>
  );

  const inactiveContent = (
    <>
      <Typography variant="h3">
        {inactiveTitle ?? (
          <FormattedMessage
            id="premium.banner.inactive.title"
            defaultMessage="Infigy Plus není aktivní"
          />
        )}
      </Typography>

      <Typography variant="body1">
        {inactiveSubtitle ?? (
          <FormattedMessage
            id="premium.banner.inactive.subtitle"
            defaultMessage="Posuňte svou správu energií na ještě vyšší úroveň. S Infigy Plus dostanete další chytré nástroje - detailní grafy, okamžité notifikace, vylepšený plánovací kalendář, přehledné reporty a další."
          />
        )}
      </Typography>

      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          size="small"
          onClick={open}
          sx={{ textTransform: 'none' }}
        >
          <FormattedMessage
            id="premium.banner.inactive.button"
            defaultMessage="Zjistit více"
          />
        </Button>
      </Box>
    </>
  );

  const premiumBannerHiddenTemporarily = useMemo(() => {
    if (!premiumBannerHiddenAt) return false;
    return differenceInDays(new Date(), new Date(premiumBannerHiddenAt)) < 7;
  }, [premiumBannerHiddenAt]);

  if (isSolidsunIframe()) return null;

  if (
    variant !== 'settings' &&
    (premiumActive ||
      premiumBannerHiddenPermanently ||
      premiumBannerHiddenTemporarily)
  ) {
    return null;
  }

  const patternBg = premiumActive;
  const mockupBg = !patternBg;

  const patternBgSx: SxProps = patternBg
    ? {
        backgroundImage: `url('${new URL(
          '../../static/pattern-bg.svg',
          import.meta.url
        ).toString()}')`,
        backgroundPosition: 'top right',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '200px auto',
      }
    : {};

  const mockupBgSx: SxProps = mockupBg
    ? {
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 300px' },
        gridTemplateRows: { xs: '160px 1fr', sm: '1fr' },
        alignItems: 'center',
        p: 0,
      }
    : {};

  return (
    <>
      <Group layout="list">
        <Paper
          sx={{
            bgcolor: colors.richBlack,
            color: colors.white,
            position: 'relative',
            overflow: 'hidden',
            ...patternBgSx,
            ...mockupBgSx,
          }}
        >
          <Stack gap={1} sx={{ ...(mockupBg ? { p: 2 } : { p: 0 }) }}>
            {premiumActive ? activeContent : inactiveContent}
          </Stack>

          {mockupBg ? (
            <Box
              sx={{
                backgroundImage: `url(${new URL(
                  '../../static/premium/mockup.jpg',
                  import.meta.url
                ).toString()})`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                width: '100%',
                height: { xs: 160, sm: '100%' },
                order: { xs: '-1', sm: '0' },
                position: 'relative',
              }}
            ></Box>
          ) : null}

          {variant !== 'settings' && (
            <IconButton
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                cursor: 'pointer',
                zIndex: 1,
                color: colors.white,
              }}
              onClick={onHide}
            >
              <Close />
            </IconButton>
          )}
        </Paper>
      </Group>

      {dialog}

      {hideDialog}
    </>
  );
};

const useHidePremiumBanner = ({
  openPremiumDialog,
}: {
  openPremiumDialog: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const onClose = () => setOpen(false);

  const dispatch = useDispatch();

  const showMore = () => {
    openPremiumDialog();
    onClose();
  };

  const showLater = () => {
    dispatch(
      variablesChange({ PREMIUM_BANNER_HIDDEN_AT: new Date().toISOString() })
    );
    onClose();
  };

  const hidePermanently = () => {
    dispatch(variablesChange({ PREMIUM_BANNER_HIDDEN_PERMANENTLY: true }));
    onClose();
  };

  const hideDialog = (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { sx: { bgcolor: 'secondary.main' } } }}
    >
      <DialogClose onClose={onClose} />
      <DialogTitle variant="h2" sx={{ textAlign: 'center', py: 3 }}>
        <FormattedMessage
          id="premium.banner.hide.confirm"
          defaultMessage="Opravdu chcete skrýt Infigy Plus?"
        />
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ textAlign: 'center', mt: 1 }}>
          <FormattedMessage
            id="premium.banner.hide.confirm.description"
            defaultMessage="Kdykoliv můžete Infigy Plus znovu prozkoumat v nastavení aplikace."
          />
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          py: 2,
          flexDirection: 'column',
          gap: 1.5,
          '& .MuiButton-root': { width: 280 },
        }}
      >
        <Button onClick={showMore} color="primary" variant="contained">
          <FormattedMessage
            id="premium.banner.hide.show_more"
            defaultMessage="Zjistit více"
          />
        </Button>
        <Button onClick={showLater} color="primary">
          <FormattedMessage
            id="premium.banner.hide.later"
            defaultMessage="Připomenout později"
          />
        </Button>
        <Button onClick={hidePermanently} variant="text">
          <FormattedMessage
            id="premium.banner.hide.yes"
            defaultMessage="Skrýt"
          />
        </Button>
      </DialogActions>
    </Dialog>
  );

  return {
    onHide: () => setOpen(true),
    hideDialog,
  };
};
